"use client";

import { useEffect, useRef, useState } from "react";

import { fetchChatHistory, sendChatMessage, deleteChatMessage, ReplyMeta } from "@/app/api/chat/api";
import { useAuth } from "@/app/hooks/context/AuthContext";
import { getEcho } from "@/app/lib/echo";
import { ChatMessage } from "@/types/chat";

const MAX_MESSAGES = 100; // cap the in-memory feed
export const CHAT_MAX_LENGTH = 280;

/**
 * Shared global casino chat hook. The client is a projection of server events:
 * syncs history via GET /chat, subscribes to the public `chat` channel, and
 * appends on every `.message` (drops on `.message-deleted`). Posting is optimistic
 * — the POST response is appended locally and the broadcast goes ->toOthers(), so
 * the sender never sees a duplicate.
 */
export function useChat() {
    const { user } = useAuth();

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Guard against double-adding a message that arrives both via the POST
    // response and (race) a broadcast.
    const seenIds = useRef<Set<number>>(new Set());

    const addMessage = (m: ChatMessage) => {
        if (seenIds.current.has(m.id)) return;
        seenIds.current.add(m.id);
        setMessages((prev) => [...prev, m].slice(-MAX_MESSAGES));
    };

    const removeMessage = (id: number) => {
        setMessages((prev) => prev.filter((m) => m.id !== id));
    };

    const resync = () => {
        fetchChatHistory()
            .then((history) => {
                seenIds.current = new Set(history.map((m) => m.id));
                setMessages(history.slice(-MAX_MESSAGES));
            })
            .catch(() => {
                /* chat history unavailable; broadcasts will still populate it */
            });
    };

    useEffect(() => {
        resync();

        const echo = getEcho();
        const channel = echo.channel("chat");
        channel.listen(".message", (m: ChatMessage) => addMessage(m));
        channel.listen(".message-deleted", (e: { id: number }) => removeMessage(e.id));
        channel.listen(".user-purged", (e: { user_id: number }) => {
            setMessages(prev => prev.filter(m => m.user_id !== e.user_id));
        });

        // Reconnect hardening: re-pull history if the socket dropped and came back.
        const conn = (echo.connector as { pusher?: { connection: { bind: (e: string, cb: () => void) => void } } }).pusher?.connection;
        let firstConnect = true;
        conn?.bind("connected", () => {
            if (firstConnect) {
                firstConnect = false;
                return;
            }
            resync();
        });

        return () => {
            echo.leaveChannel("chat");
        };
    }, []);

    const send = async (raw: string, reply?: ReplyMeta) => {
        const text = raw.trim();
        if (!user) {
            setError("Log in to chat.");
            return false;
        }
        if (text === "" || sending) return false;
        if (text.length > CHAT_MAX_LENGTH) {
            setError(`Max ${CHAT_MAX_LENGTH} characters.`);
            return false;
        }

        setSending(true);
        setError(null);
        try {
            const socketId = getEcho().socketId();
            const msg = await sendChatMessage(text, socketId, reply);
            addMessage(msg);
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to send message.");
            return false;
        } finally {
            setSending(false);
        }
    };

    const remove = async (id: number) => {
        // Optimistic removal; the broadcast confirms it for everyone else.
        removeMessage(id);
        try {
            await deleteChatMessage(id);
        } catch {
            resync(); // restore on failure
        }
    };

    const canModerate = user?.role === "admin" || user?.role === "mod";

    return {
        messages,
        sending,
        error,
        loggedIn: !!user,
        canModerate,
        currentUserId: user?.id ?? null,
        send,
        remove,
    };
}
